import React, { useState, useEffect } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import EmailVerificationForm from './EmailVerificationForm';
import UserDetailsForm from './UserDetailsForm';
import { useLanguage } from './LanguageContext';
import './HomePage.css';
import { useNavigate } from 'react-router-dom';
import enUS from '../locales/en-US.json';
import { api } from '../services/api';

const LOCAL_STORAGE_KEY = 'izypostOnboardingForm';
const STATE_STORAGE_KEY = 'izypostOnboardingState';


const HomePage: React.FC = () => {
  usePageTitle('Home - IZYPOS Onboarding');
  const { t } = useLanguage();
  const navigate = useNavigate();
  // Restore isVerified and verifiedEmail from sessionStorage
  const getInitialState = () => {
    if (typeof window === 'undefined') return { isVerified: false, verifiedEmail: '' };
    try {
      const saved = sessionStorage.getItem(STATE_STORAGE_KEY);
      if (saved) return JSON.parse(saved);
    } catch {}
    return { isVerified: false, verifiedEmail: '' };
  };
  const [isVerified, setIsVerified] = useState(() => getInitialState().isVerified);
  const [verifiedEmail, setVerifiedEmail] = useState(() => getInitialState().verifiedEmail);
  const [userDetailsComplete, setUserDetailsComplete] = useState(false);
  const [productsSelected, setProductsSelected] = useState(false);
  const [onboardingSuccess, setOnboardingSuccess] = useState(false);

  // Move updateOnboardingState inside component to access state
  const updateOnboardingState = (newState: Record<string, unknown>) => {
    const merged = { isVerified, verifiedEmail, ...newState };
    sessionStorage.setItem(STATE_STORAGE_KEY, JSON.stringify(merged));
  };

  const handleVerificationSuccess = (email?: string) => {
    setIsVerified(true);
    if (email) setVerifiedEmail(email);
    updateOnboardingState({ isVerified: true, verifiedEmail: email || '' });
  };

  // Keep state in sync with sessionStorage
  useEffect(() => {
    updateOnboardingState({ isVerified, verifiedEmail });
  }, [isVerified, verifiedEmail]);

  // Optionally clear onboarding state on final success
  useEffect(() => {
    if (productsSelected && !onboardingSuccess) {
      setOnboardingSuccess(true);
      localStorage.removeItem(LOCAL_STORAGE_KEY);
      sessionStorage.removeItem(STATE_STORAGE_KEY);
    }
  }, [productsSelected, onboardingSuccess]);

  // Scroll to top on step change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [isVerified, userDetailsComplete, productsSelected, onboardingSuccess]);

  return (
    <div className="main-content">
      <div className="home-page">
        {!isVerified ? (
          <div className="welcome-section">
            <h1>IZYPOS Onboarding</h1>
            <EmailVerificationForm
              onSuccess={email => handleVerificationSuccess(email)}
              onSendOtp={async (email) => { await api.sendOtp(email); }}
              onVerifyOtp={async (email, otp) => { await api.verifyOtp(email, otp); }}
              onCheckEmail={api.checkEmail}
            />
          </div>
        ) : !userDetailsComplete ? (
          <UserDetailsForm
            email={verifiedEmail}
            onComplete={() => setUserDetailsComplete(true)}
            onSubmitOnboarding={api.submitOnboardingData}
          />
        ) : null }
      </div>
    </div>
  );
};

export default HomePage; 